/** The connection, when it is not there (lib/connectivity.ts): a slim bar
 *  at the top saying so, that it keeps trying, and a way to try now; then
 *  "Back online" for a moment. What is on screen stays. */
import { useEffect, useState } from "react";
import { CloudOff, RotateCw, Wifi, WifiOff } from "lucide-react";
import { getConnection, probe, subscribeConnection, type Connection } from "../lib/connectivity";
import { useAuth } from "../lib/auth";

export function ConnectionBanner(): JSX.Element | null {
  const [conn, setConn] = useState<Connection>(getConnection);
  const [back, setBack] = useState(false);
  const [checking, setChecking] = useState(false);
  const auth = useAuth();
  useEffect(
    () =>
      subscribeConnection((c) => {
        setConn((prev) => {
          if (c === "online" && prev !== "online") setBack(true);
          return c;
        });
      }),
    [],
  );
  useEffect(() => {
    if (!back) return;
    const t = window.setTimeout(() => setBack(false), 2_500);
    return () => window.clearTimeout(t);
  }, [back]);

  if (conn === "online" && !back) return null;
  // The offline screen (AuthGate) already says it, full size.
  if (conn !== "online" && auth.unreachable && !auth.authenticated) return null;
  const offline = conn === "offline";
  return (
    <div role="status" aria-live="polite" className="pointer-events-none fixed inset-x-0 z-[200] flex justify-center px-4" style={{ top: "calc(env(safe-area-inset-top, 0px) + 8px)" }}>
      {conn === "online" ? (
        <div className="wov-fade pointer-events-auto flex items-center gap-2 rounded-full bg-emerald-600 px-3.5 py-1.5 text-[12.5px] font-semibold text-white shadow-lg">
          <Wifi className="h-3.5 w-3.5" /> Back online
        </div>
      ) : (
        <div className="wov-fade pointer-events-auto flex max-w-xl items-center gap-2.5 rounded-full bg-slate-900/95 py-1.5 pl-3.5 pr-1.5 text-[12.5px] text-white shadow-lg ring-1 ring-white/10 dark:bg-slate-800/95">
          {offline ? <WifiOff className="h-3.5 w-3.5 flex-shrink-0 text-amber-300" /> : <CloudOff className="h-3.5 w-3.5 flex-shrink-0 text-amber-300" />}
          <span className="min-w-0 truncate">
            <span className="font-semibold">{offline ? "You're offline." : "Can't reach Waki Mail."}</span>{" "}
            <span className="text-slate-300">What's on screen stays; it reconnects by itself.</span>
          </span>
          <button
            type="button"
            disabled={checking}
            onClick={async () => {
              setChecking(true);
              await probe();
              setChecking(false);
            }}
            className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 font-semibold hover:bg-white/20 disabled:opacity-60"
          >
            <RotateCw className={"h-3 w-3 " + (checking ? "animate-spin" : "")} /> Try now
          </button>
        </div>
      )}
    </div>
  );
}
